/**
 * Instant Search Service
 *
 * Business logic layer for ISW operations.
 *
 * **Migration from Legacy**:
 * - Legacy: requestSuggestion(), buildDefaultDataSuggestion(), getSuggestProducts(), etc. in index.js
 * - New: Injectable service with clear separation of concerns
 *
 * @module instant-search.service
 */

import { Injectable } from 'framework/di';

import type { ProductItem } from '../../types';
import { AppService } from '../app/app.service';
import type { PredictiveBundleData } from '../predictive-bundle/predictive-bundle.model';
import { PredictiveBundleService } from '../predictive-bundle/predictive-bundle.service';
import type { VolumeBundleData } from '../volume-bundle/volume-bundle.model';
import { VolumeBundleService } from '../volume-bundle/volume-bundle.service';
import { InstantSearchAPI } from './instant-search.api';
import { InstantSearchSelectors } from './instant-search.selectors';
import type {
  DefaultSuggestionData,
  RecentSearchItem,
  SearchSettings,
  SuggestionBundle,
  SuggestionData,
  SuggestionResponse,
} from './instant-search.types';
import { RecentSearchService } from './recent-search.service';

/**
 * Structure for cached suggestion data.
 */
interface CachedSuggestionData {
  query: string;
  id: string;
  suggestions: string[];
}

/**
 * Price formatting settings from theme configuration.
 */
interface PriceFormatSettings {
  showCentAsSuperscript?: boolean;
  showCurrencyCodes?: boolean;
}

/**
 * Service for instant search business logic.
 *
 * Technical Support teams can extend this service for custom search behavior:
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('InstantSearchService', (InstantSearchService) => {
 *   return class CustomSearchService extends InstantSearchService {
 *     // Filter products before rendering — preferred extension point
 *     protected filterWidgetProducts(products, searchSettings) {
 *       const available = products.filter(p => p.available);
 *       return super.filterWidgetProducts(available, searchSettings);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class InstantSearchService {
  /** localStorage key for suggestion data caching. */
  static readonly SUGGESTION_DATA_KEY = 'boostPFSSuggestionData';
  /** localStorage key for analytics pre-request IDs. */
  static readonly PRE_REQUEST_IDS = 'boostSdPreRequestIds';

  /** Debounce timer ID */
  protected debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Debounce delay in milliseconds */
  protected readonly DEBOUNCE_DELAY = 300;
  /** Cached redirects map */
  protected redirectsCache: Record<string, string> | null = null;
  /** Debug mode flag */
  protected debugMode = false;
  /** Current search abort controller */
  protected abortController: AbortController | null = null;

  constructor(
    protected appService: AppService,
    protected instantSearchAPI: InstantSearchAPI,
    protected instantSearchSelectors: InstantSearchSelectors,
    protected recentSearchService: RecentSearchService,
    protected predictiveBundleService: PredictiveBundleService,
    protected volumeBundleService: VolumeBundleService
  ) {}

  /**
   * Enable or disable debug logging.
   *
   * @param enabled - Whether to enable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Log debug messages when debug mode is enabled.
   *
   * @param message - Message to log
   * @param data - Optional data to include
   */
  protected log(message: string, data?: unknown): void {
    if (this.debugMode) {
      this.appService.logger.debug(`[InstantSearchService] ${message}`, data ?? '');
    }
  }

  /**
   * Execute search with debouncing.
   *
   * **Legacy**: requestSuggestionDebounce() using debounce utility
   * **New**: Method with built-in debouncing and Promise support
   *
   * @param query - Search query
   * @param searchSettings - Search settings
   * @returns Promise resolving to suggestion data
   */
  searchDebounced(query: string, searchSettings: SearchSettings): Promise<SuggestionResponse> {
    return new Promise((resolve, reject) => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(async () => {
        try {
          const result = await this.search(query, searchSettings);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, this.DEBOUNCE_DELAY);
    });
  }

  /**
   * Cancel any pending debounced search.
   */
  cancelSearch(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.log('cancelSearch: debounced search cancelled');
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.log('cancelSearch: active request aborted');
    }
  }

  /**
   * Execute search immediately.
   *
   * **Legacy**: requestSuggestion(context, query)
   * **New**: search(query, searchSettings) with typed response
   *
   * @param query - Search query
   * @param searchSettings - Search settings
   * @returns Promise resolving to suggestion data
   */
  async search(query: string, searchSettings: SearchSettings): Promise<SuggestionResponse> {
    // Apply config processing hook first — TS teams can normalise settings here
    const processedSettings = this.processWidgetConfig(searchSettings);

    this.log('search', { query, searchSettings: processedSettings.suggestionStyle });

    // Abort any in-flight request before starting a new one
    if (this.abortController) {
      this.abortController.abort();
    }

    // Create new abort controller for this request
    this.abortController = new AbortController();

    // Fetch suggestions from API
    let data = await this.instantSearchAPI.getSuggestions(
      { q: query },
      processedSettings,
      undefined,
      this.abortController.signal
    );

    // Save request ID for analytics — correlates product clicks with this search request
    this.saveRequestId('suggest', data.meta?.rid, data.bundles);

    // Validate response — TS teams can reject responses that don't meet shop rules
    if (!this.validateWidgetData(data)) {
      this.log('search: validateWidgetData returned false, treating as empty');
      data = {
        ...data,
        all_empty: true,
        products: [],
        suggestions: [],
        collections: [],
        pages: [],
      };
    }

    // Handle no results - fetch fallback products
    if (data.all_empty) {
      this.log('search: all_empty, fetching fallback products');
      data = await this.handleNoResults(data, query, processedSettings);
    } else {
      // Transform and filter products via the overridable extension hook
      data.products = this.filterWidgetProducts(data.products, processedSettings);

      // Check if all visible blocks are empty
      const allBlocksEmpty = this.areAllBlocksEmpty(data, processedSettings);
      if (allBlocksEmpty) {
        this.log('search: all blocks empty, fetching fallback products');
        data = await this.handleNoResults(data, query, processedSettings);
      } else {
        // Cache suggestion data for analytics
        this.cacheSuggestionData(query, data);
      }
    }

    // Handle bundles if present
    if (data.bundles && data.bundles.length > 0) {
      this.log('search: processing bundles', data.bundles.length);
      data = await this.processBundles(data, processedSettings);
    }

    return data;
  }

  /**
   * Handle no-results scenario by fetching fallback products.
   *
   * @param data - Current suggestion data
   * @param query - Search query
   * @param searchSettings - Search settings
   * @returns Updated suggestion data with fallback products
   */
  protected async handleNoResults(
    data: SuggestionResponse,
    query: string,
    searchSettings: SearchSettings
  ): Promise<SuggestionResponse> {
    const noResultSettings = searchSettings.suggestionNoResult?.products;

    if (noResultSettings?.status && noResultSettings.data?.length > 0) {
      this.log('handleNoResults: fetching fallback products', noResultSettings.data);

      const products = await this.instantSearchAPI.getProductsByHandle(noResultSettings.data, {
        query,
        productAvailable: searchSettings.productAvailable,
      });

      if (products.length > 0) {
        data.products = this.filterWidgetProducts(products, searchSettings);
      }
    }

    return data;
  }

  /**
   * Check if all visible suggestion blocks are empty.
   *
   * @param data - Suggestion data
   * @param searchSettings - Search settings
   * @returns True if all blocks are empty
   */
  protected areAllBlocksEmpty(data: SuggestionResponse, searchSettings: SearchSettings): boolean {
    const blocks = searchSettings.suggestionBlocks || [];
    const disabledBlocks: Record<string, boolean> = {};

    blocks.forEach((block) => {
      if (block.status !== 'active') {
        disabledBlocks[block.type] = true;
      }
    });

    const hasProducts = data.products?.length > 0 && !disabledBlocks.products;
    const hasSuggestions = data.suggestions?.length > 0 && !disabledBlocks.suggestions;
    const hasCollections = data.collections?.length > 0 && !disabledBlocks.collections;
    const hasPages = data.pages?.length > 0 && !disabledBlocks.pages;

    return !hasProducts && !hasSuggestions && !hasCollections && !hasPages;
  }

  /**
   * Cache suggestion data in localStorage for analytics.
   *
   * **Legacy**: setLocalStorage(SUGGESTION_DATA, { query, id, suggestions })
   * **New**: Dedicated method with typed structure
   *
   * @param query - Search query
   * @param data - Suggestion response data
   */
  protected cacheSuggestionData(query: string, data: SuggestionResponse): void {
    try {
      const cacheData: CachedSuggestionData = {
        query,
        id: data.meta?.rid || '',
        suggestions: data.suggestions || [],
      };

      localStorage.setItem(InstantSearchService.SUGGESTION_DATA_KEY, JSON.stringify(cacheData));
      this.log('cacheSuggestionData', cacheData);
    } catch (error) {
      this.log('cacheSuggestionData: failed', error);
    }
  }

  /**
   * Save request ID to localStorage for analytics tracking.
   *
   * Stores the API request ID under the `suggest` key in `boostSdPreRequestIds`
   * so that downstream analytics can attribute product-click events to this
   * specific search request.
   *
   * **Legacy**: `saveRequestId('suggest', requestId, bundles)` in `@deprecation/api/search.js`
   * **New**: Protected method on the service, called immediately after `getSuggestions()`
   *
   * @param type - Request type (always 'suggest' for ISW)
   * @param requestId - The `rid` value from the API response metadata
   * @param bundles - Optional bundles array for widget-specific placement tracking
   */
  protected saveRequestId(type: 'suggest', requestId?: string, bundles?: SuggestionBundle[]): void {
    if (!requestId) return;

    try {
      const stored = localStorage.getItem(InstantSearchService.PRE_REQUEST_IDS);
      const requestIds: Record<string, string> = stored
        ? (JSON.parse(stored) as Record<string, string>)
        : {};

      requestIds[type] = requestId;

      if (Array.isArray(bundles)) {
        bundles.forEach((bundle) => {
          if (bundle?.widgetId) {
            requestIds[`${bundle.widgetId}_instant_search`] = requestId;
          }
        });
      }

      localStorage.setItem(InstantSearchService.PRE_REQUEST_IDS, JSON.stringify(requestIds));
      this.log('saveRequestId', { type, requestId });
    } catch (error) {
      this.log('saveRequestId: failed', error);
    }
  }

  /**
   * Get cached suggestion data from localStorage.
   *
   * @returns Cached suggestion data or null
   */
  getCachedSuggestionData(): CachedSuggestionData | null {
    try {
      const cached = localStorage.getItem(InstantSearchService.SUGGESTION_DATA_KEY);
      if (cached) {
        return JSON.parse(cached) as CachedSuggestionData;
      }
    } catch (error) {
      this.log('getCachedSuggestionData: failed', error);
    }
    return null;
  }

  /**
   * Process bundle data from search response.
   *
   * **Legacy**: Inline bundle processing with volumeBundleData/dynamicBundleData
   * **New**: Delegated to bundle services (when integrated)
   *
   * @param data - Suggestion data with bundles
   * @param _searchSettings - Search settings
   * @returns Updated data with processed bundles
   */
  protected async processBundles(
    data: SuggestionResponse,
    _searchSettings: SearchSettings
  ): Promise<SuggestionResponse> {
    if (!data.bundles || data.bundles.length === 0) {
      return data;
    }

    this.log('processBundles: processing', data.bundles.length);

    const widgetDesignSettingsMap: Record<string, Record<string, unknown>> = {};

    await Promise.all(
      data.bundles.map(async (bundle) => {
        if (bundle.bundle_type === 'volume') {
          const volumeBundle = bundle as unknown as VolumeBundleData;
          const processedData = await this.volumeBundleService.processVolumeBundleData(
            volumeBundle,
            {
              optionData: data.products as unknown as { variantId?: string | number },
              target: document?.getElementById('bc-instant-search'),
              position: 'isw',
            }
          );

          if (processedData) {
            (data as SuggestionResponse & { bundle?: unknown }).bundle = processedData;
          }

          const settings = this.volumeBundleService.getWidgetSettings(bundle.widgetId);
          widgetDesignSettingsMap[bundle.widgetId] = {
            ...settings.widgetDesignSettings,
          };
        } else {
          const predictiveBundle = bundle as unknown as PredictiveBundleData;
          const calculatedData = this.predictiveBundleService.calculateBundleData(predictiveBundle);

          (data as SuggestionResponse & { bundle?: unknown }).bundle = calculatedData;

          const settings = this.predictiveBundleService.getWidgetSettings(bundle.widgetId);
          widgetDesignSettingsMap[bundle.widgetId] = {
            ...settings.widgetDesignSettings,
          };

          Object.assign(bundle, {
            ...calculatedData,
          });
        }
      })
    );

    (
      data as SuggestionResponse & {
        widgetDesignSettingsMap?: Record<string, Record<string, unknown>>;
      }
    ).widgetDesignSettingsMap = widgetDesignSettingsMap;
    (
      data as SuggestionResponse & { widgetDesignSettings?: Record<string, unknown> }
    ).widgetDesignSettings = widgetDesignSettingsMap[data.bundles[0]?.widgetId] || {};

    return data;
  }

  /**
   * Build default suggestion data for initial state.
   *
   * **Legacy**: buildDefaultDataSuggestion(context)
   * **New**: getDefaultSuggestionData(searchSettings) with typed return
   *
   * @param searchSettings - Search settings
   * @returns Promise resolving to default suggestion data
   */
  async getDefaultSuggestionData(searchSettings: SearchSettings): Promise<DefaultSuggestionData> {
    // Apply config processing hook — TS teams can inject defaults here
    const processedSettings = this.processWidgetConfig(searchSettings);

    this.log('getDefaultSuggestionData', processedSettings.suggestionStyle);

    // Get redirects
    const redirects = await this.getRedirects();

    // Get suggested products on click
    let products: ProductItem[] = [];
    if (processedSettings.searchBoxOnclick?.productSuggestion?.status) {
      products = await this.getSuggestProducts(processedSettings);
    }

    // Get recent searches
    const recentSearches = this.recentSearchService.getRecentSearches(
      processedSettings.searchBoxOnclick?.recentSearch?.number || 10
    );

    return {
      // Settings
      suggestionMobileStyle: processedSettings.suggestionMobileStyle || 'style1',
      suggestionMobileProductItemType: processedSettings.suggestionMobileProductItemType || 'grid',
      redirects,

      // State
      isLoading: false,
      money_format: this.appService.TAEAppConfig.shop?.money_format || '',
      money_format_with_currency:
        this.appService.TAEAppConfig.shop?.money_format_with_currency || '',

      // Data
      all_empty: true,
      collections: [],
      pages: [],
      query: '',
      suggestions: [],
      total_product: 0,
      products,

      // Recent & Suggestions
      recentSearches,
      searchTermSuggestionList:
        processedSettings.searchBoxOnclick?.searchTermSuggestion?.data || [],
      searchTermSuggestionStatus:
        processedSettings.searchBoxOnclick?.searchTermSuggestion?.status || false,
      searchTermSuggestionLabel:
        processedSettings.searchBoxOnclick?.searchTermSuggestion?.label || '',
      searchTermNoResultList: processedSettings.suggestionNoResult?.search_terms?.data || [],
      searchTermNoResultStatus: processedSettings.suggestionNoResult?.search_terms?.status || false,
      searchTermNoResultLabel: processedSettings.suggestionNoResult?.search_terms?.label || '',
      suggestionBlocksList: processedSettings.suggestionBlocks || [],
    };
  }

  /**
   * Get suggested products for search box on-click.
   *
   * **Legacy**: getSuggestProducts(context)
   * **New**: getSuggestProducts(searchSettings) with typed return
   *
   * @param searchSettings - Search settings
   * @returns Promise resolving to array of products
   */
  async getSuggestProducts(searchSettings: SearchSettings): Promise<ProductItem[]> {
    const productIds = searchSettings.searchBoxOnclick?.productSuggestion?.data || [];
    if (productIds.length === 0) return [];

    this.log('getSuggestProducts', productIds);

    const products = await this.instantSearchAPI.getProductsById(productIds);
    return this.filterWidgetProducts(products, searchSettings);
  }

  /**
   * Get redirect mappings.
   *
   * @returns Promise resolving to redirect map
   */
  async getRedirects(): Promise<Record<string, string>> {
    if (this.redirectsCache) {
      this.log('getRedirects: using cache');
      return this.redirectsCache;
    }

    this.redirectsCache = await this.instantSearchAPI.getRedirects();
    this.log('getRedirects: fetched', Object.keys(this.redirectsCache).length);
    return this.redirectsCache;
  }

  /**
   * Clear redirects cache to force re-fetch.
   */
  clearRedirectsCache(): void {
    this.redirectsCache = null;
    this.log('clearRedirectsCache: cache cleared');
  }

  /**
   * Check if query should redirect.
   *
   * **Legacy**: Inline check in handleRedirect()
   * **New**: Dedicated method with type safety
   *
   * @param query - Search query
   * @param redirects - Redirect mappings
   * @returns Redirect URL or null
   */
  checkRedirect(query: string, redirects: Record<string, string>): string | null {
    if (!query || !redirects) return null;

    const normalizedQuery = query.trim().toLowerCase();

    for (const [term, url] of Object.entries(redirects)) {
      if (term.toLowerCase() === normalizedQuery) {
        this.log('checkRedirect: match found', { query, url });
        return url;
      }
    }

    return null;
  }

  /**
   * Handle redirect if query matches.
   *
   * @param query - Search query
   * @param redirects - Redirect mappings
   * @returns True if redirect was triggered
   */
  handleRedirect(query: string, redirects: Record<string, string>): boolean {
    const url = this.checkRedirect(query, redirects);

    if (url) {
      this.log('handleRedirect: redirecting', url);
      window.location.href = url;
      return true;
    }

    return false;
  }

  /**
   * Get price formatting settings from theme configuration.
   *
   * @returns Price format settings
   */
  protected getPriceFormatSettings(): PriceFormatSettings {
    const priceConfig =
      this.appService.TAEAppConfig.templateSettings?.themeSettings?.productItems?.productInfo
        ?.elements?.price || {};

    return {
      showCentAsSuperscript: priceConfig.showCentAsSuperscript,
      showCurrencyCodes: priceConfig.showCurrencyCodes,
    };
  }

  /**
   * Transform product prices for display.
   *
   * **Legacy**: transformProductPrice(context, products)
   * **New**: transformProductPrices(products, searchSettings) using AppService
   *
   * @param products - Array of products
   * @param _searchSettings - Search settings (for future use)
   * @returns Products with formatted prices
   */
  transformProductPrices(products: ProductItem[], _searchSettings?: SearchSettings): ProductItem[] {
    if (!products || products.length === 0) return [];

    const priceSettings = this.getPriceFormatSettings();
    const { showCentAsSuperscript, showCurrencyCodes } = priceSettings;

    return products.map((product) => {
      const priceMin = Number(product.price_min) || 0;
      const compareAtPriceMin = Number(product.compare_at_price_min) || 0;
      const saleAmount = compareAtPriceMin > priceMin ? compareAtPriceMin - priceMin : 0;

      return {
        ...product,
        price_min_format: this.appService.formatCurrency(priceMin, {
          showCurrencyCodes,
          showCentAsSuperscript,
          removeDecimalPoint: showCentAsSuperscript,
        }),
        compare_at_price_min_format: this.appService.formatCurrency(compareAtPriceMin, {
          showCurrencyCodes,
          showCentAsSuperscript,
          removeDecimalPoint: showCentAsSuperscript,
        }),
        sale_amount_format: this.appService.formatCurrency(saleAmount, {
          showCurrencyCodes: false,
          showCentAsSuperscript,
          removeDecimalPoint: showCentAsSuperscript,
        }),
      };
    });
  }

  /**
   * Generate search page URL.
   *
   * **Legacy**: generateSearchPageHref(term, extraParam)
   * **New**: getSearchPageUrl(query, extraParams) with proper encoding
   *
   * @param query - Search query
   * @param extraParams - Additional URL parameters
   * @returns Search page URL
   */
  getSearchPageUrl(query: string = '', extraParams: string = ''): string {
    const encodedQuery = encodeURIComponent(query).replace(/%20/g, '+');
    const params = extraParams ? `&${extraParams.replace(/&/g, 'AND')}` : '';

    // Build URL with localization
    const locale = window.Shopify?.routes?.root || '/';
    const baseUrl = locale.endsWith('/') ? locale : `${locale}/`;

    const url = `${baseUrl}search?q=${encodedQuery}${params}`;
    this.log('getSearchPageUrl', { query, url });

    return url;
  }

  /**
   * Add recent search entry.
   *
   * @param title - Search term title
   * @param extraParam - Optional extra parameters
   */
  addRecentSearch(title: string, extraParam?: string): void {
    this.recentSearchService.addRecentSearch(title, extraParam);
    this.log('addRecentSearch', { title, extraParam });
  }

  /**
   * Get recent searches.
   *
   * @param limit - Maximum number of recent searches
   * @returns Array of recent search items
   */
  getRecentSearches(limit?: number): RecentSearchItem[] {
    return this.recentSearchService.getRecentSearches(limit);
  }

  /**
   * Clear all recent searches.
   */
  clearRecentSearches(): void {
    this.recentSearchService.clearRecentSearches();
    this.log('clearRecentSearches: cleared');
  }

  /**
   * Render suggestions using template.
   *
   * @param template - ISW template string
   * @param data - Suggestion data to render
   * @returns Promise resolving to rendered HTML
   */
  async renderSuggestions(template: string, data: SuggestionData): Promise<string> {
    if (!template) {
      this.appService.logger.warn('Instant search template is required for rendering');
      return '';
    }

    this.log('renderSuggestions', { query: data.query, productsCount: data.products?.length });

    try {
      const shopifyWindow = window as unknown as {
        Shopify?: { routes?: { root?: string } };
      };
      const locale = (shopifyWindow.Shopify?.routes?.root || '/').replace(/\/$/, '');

      const renderData = { ...data, locale };

      const html = await this.appService.templateRender(template, renderData);
      return html;
    } catch (error) {
      this.appService.logger.error('Failed to render instant search suggestions', error);
      return '';
    }
  }

  /**
   * Merge default data with search response for rendering.
   *
   * @param defaultData - Default suggestion data
   * @param searchResponse - Search API response
   * @returns Merged suggestion data
   */
  mergeSuggestionData(
    defaultData: DefaultSuggestionData,
    searchResponse: SuggestionResponse
  ): SuggestionData {
    return {
      ...defaultData,
      ...searchResponse,
      isLoading: false,
    };
  }

  /**
   * Process widget configuration before use.
   *
   * Extension point for TS teams to customise search settings on a per-shop
   * basis before any search or default-data operations begin.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchService', (InstantSearchService) => {
   *   return class CustomService extends InstantSearchService {
   *     protected processWidgetConfig(config) {
   *       // Enforce a minimum product limit for this shop
   *       return {
   *         ...config,
   *         suggestionBlocks: config.suggestionBlocks?.map(b =>
   *           b.type === 'products' ? { ...b, limit: Math.max(b.limit ?? 4, 6) } : b
   *         ),
   *       };
   *     }
   *   };
   * });
   * ```
   *
   * @param config - Raw search settings from the block context
   * @returns Processed search settings (identity by default)
   */
  protected processWidgetConfig(config: SearchSettings): SearchSettings {
    return config;
  }

  /**
   * Validate suggestion data before caching or rendering.
   *
   * Extension point for TS teams to add custom validation rules.
   * Return `false` to skip rendering and treat the response as empty.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchService', (InstantSearchService) => {
   *   return class CustomService extends InstantSearchService {
   *     protected validateWidgetData(data) {
   *       // Require at least one result type to be present
   *       return (data.products?.length ?? 0) > 0 || (data.suggestions?.length ?? 0) > 0;
   *     }
   *   };
   * });
   * ```
   *
   * @param data - Raw suggestion response from the API
   * @returns True if the data should be used, false to discard it
   */
  protected validateWidgetData(data: SuggestionResponse): boolean {
    return data !== null && data !== undefined;
  }

  /**
   * Filter and transform products for display.
   *
   * Extension point for TS teams to add shop-specific product filtering.
   * The base implementation transforms product prices via `transformProductPrices()` —
   * override to add additional filters (availability, tags, vendor, metafields, etc.).
   *
   * **Legacy**: `transformProductPrice(context, products)` — not overridable
   * **New**: Protected method with full override capability
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchService', (InstantSearchService) => {
   *   return class CustomService extends InstantSearchService {
   *     protected filterWidgetProducts(products, searchSettings) {
   *       // Only show in-stock products in ISW results
   *       const available = products.filter(p => p.available);
   *       return super.filterWidgetProducts(available, searchSettings);
   *     }
   *   };
   * });
   * ```
   *
   * @param products - Raw product array from the API
   * @param searchSettings - Current search settings
   * @returns Filtered and price-transformed product array
   */
  protected filterWidgetProducts(
    products: ProductItem[],
    searchSettings: SearchSettings
  ): ProductItem[] {
    const baseFiltered = this.transformProductPrices(products, searchSettings);
    return this.applyShopSpecificFilters(baseFiltered, searchSettings);
  }

  /**
   * Apply shop-specific product filters after base price transformation.
   *
   * Second-level extension hook called by `filterWidgetProducts()`. Override to
   * add availability checks, tag filters, vendor rules, metafield filtering, etc.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchService', (InstantSearchService) => {
   *   return class CustomService extends InstantSearchService {
   *     protected applyShopSpecificFilters(products, searchSettings) {
   *       // Hide gift-card products from ISW results
   *       return products.filter(p => p.product_type !== 'Gift Cards');
   *     }
   *   };
   * });
   * ```
   *
   * @param products - Products already price-transformed by `filterWidgetProducts()`
   * @param _searchSettings - Current search settings (available for condition logic)
   * @returns Filtered product array (identity by default)
   */
  protected applyShopSpecificFilters(
    products: ProductItem[],
    _searchSettings: SearchSettings
  ): ProductItem[] {
    return products;
  }

  /**
   * Cleanup resources.
   */
  dispose(): void {
    this.cancelSearch();
    this.redirectsCache = null;
    this.log('dispose: cleanup complete');
  }
}
